/**
 * PhysicsWorld - Planck.js 物理世界封装
 * @description 管理物理模拟的核心类，封装 planck.World
 */

import { Vec2, World, Body, Circle, Edge } from 'planck';
import type { BodyDef, FixtureDef } from 'planck';

/**
 * 物理世界配置选项
 */
export interface PhysicsWorldConfig {
  /** 重力向量，默认为 Vec2(0, -10) */
  gravity?: Vec2;
  /** 允许休眠，默认 true */
  allowSleep?: boolean;
}

/**
 * 物理对象创建选项
 */
export interface BodyOptions {
  /** 位置 x */
  x: number;
  /** 位置 y */
  y: number;
  /** 类型：'static' | 'dynamic' | 'kinematic' */
  type?: 'static' | 'dynamic' | 'kinematic';
  /** 圆形半径（创建圆形时使用） */
  radius?: number;
  /** 密度 */
  density?: number;
  /** 摩擦系数 */
  friction?: number;
  /** 恢复系数（弹性） */
  restitution?: number;
  /** 是否为边界（创建边界时使用） */
  isBoundary?: boolean;
  /** 边界尺寸（创建边界时使用） */
  boundarySize?: { width: number; height: number };
}

/**
 * PhysicsWorld 类
 * 封装 Planck.js 的 World 实例，提供物理模拟管理
 */
export class PhysicsWorld {
  private world: World;
  private bodies: Map<string, Body> = new Map();
  private timeStep: number = 1 / 60; // 60fps
  private velocityIterations: number = 6;
  private positionIterations: number = 2;

  /**
   * 创建物理世界实例
   * @param config 物理世界配置
   */
  constructor(config?: PhysicsWorldConfig) {
    const gravity = config?.gravity ?? Vec2(0, -10);
    this.world = World(gravity);

    // 设置默认允许休眠
    if (config?.allowSleep !== undefined) {
      this.world.setAllowSleeping(config.allowSleep);
    }
  }

  /**
   * 获取底层的 planck.World 实例
   * @returns World 实例
   */
  getWorld(): World {
    return this.world;
  }

  /**
   * 执行物理步进
   * @param dt 时间增量（秒），默认为 1/60
   */
  step(dt?: number): void {
    const deltaTime = dt ?? this.timeStep;
    this.world.step(deltaTime, this.velocityIterations, this.positionIterations);
  }

  /**
   * 创建物理对象
   * @param id 对象唯一标识
   * @param options 创建选项
   * @returns 创建的 Body 实例
   */
  createBody(id: string, options: BodyOptions): Body {
    // 创建物体定义
    const bodyDef: BodyDef = {
      position: Vec2(options.x, options.y),
      type: this.parseBodyType(options.type ?? 'dynamic'),
    };

    const body = this.world.createBody(bodyDef);

    // 如果是边界
    if (options.isBoundary && options.boundarySize) {
      this.createBoundaryFixtures(body, options.boundarySize);
    }
    // 如果是圆形
    else if (options.radius) {
      const fixtureDef: FixtureDef = {
        shape: Circle(options.radius),
        density: options.density ?? 1.0,
        friction: options.friction ?? 0.3,
        restitution: options.restitution ?? 0.5,
      };
      body.createFixture(fixtureDef);
    }

    this.bodies.set(id, body);
    return body;
  }

  /**
   * 获取物理对象
   * @param id 对象标识
   * @returns Body 实例或 undefined
   */
  getBody(id: string): Body | undefined {
    return this.bodies.get(id);
  }

  /**
   * 获取对象位置
   * @param id 对象标识
   * @returns 位置 { x, y } 或 null
   */
  getPosition(id: string): { x: number; y: number } | null {
    const body = this.bodies.get(id);
    if (!body) return null;
    const pos = body.getPosition();
    return { x: pos.x, y: pos.y };
  }

  /**
   * 删除物理对象
   * @param id 对象标识
   */
  destroyBody(id: string): void {
    const body = this.bodies.get(id);
    if (body) {
      this.world.destroyBody(body);
      this.bodies.delete(id);
    }
  }

  /**
   * 清除所有物理对象
   */
  clear(): void {
    // 遍历所有 body 并销毁
    for (const body of this.bodies.values()) {
      this.world.destroyBody(body);
    }
    this.bodies.clear();
  }

  /**
   * 获取所有对象数量
   */
  getBodyCount(): number {
    return this.bodies.size;
  }

  /**
   * 解析物体类型
   */
  private parseBodyType(type: 'static' | 'dynamic' | 'kinematic'): 'static' | 'dynamic' | 'kinematic' {
    return type;
  }

  /**
   * 创建边界 Fixtures（地面、墙壁）
   * @param body 物体实例
   * @param size 边界尺寸
   */
  private createBoundaryFixtures(body: Body, size: { width: number; height: number }): void {
    const { width, height } = size;
    const halfWidth = width / 2;

    // 地面（底部）
    const groundFixtureDef: FixtureDef = {
      shape: Edge(Vec2(-halfWidth, 0), Vec2(halfWidth, 0)),
      friction: 0.5,
    };
    body.createFixture(groundFixtureDef);

    // 左墙
    const leftWallDef: FixtureDef = {
      shape: Edge(Vec2(0, 0), Vec2(0, height)),
      friction: 0.5,
    };
    body.createFixture(leftWallDef);

    // 右墙
    const rightWallDef: FixtureDef = {
      shape: Edge(Vec2(width, 0), Vec2(width, height)),
      friction: 0.5,
    };
    body.createFixture(rightWallDef);
  }
}

// 导出 planck 工具函数和类
export { Vec2, Circle, Edge };
export type { Body };